import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import { motion, AnimatePresence } from "framer-motion";
import RFLogo from "./RFLogo";

const NavMain = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const navItems = [
    { name: "Home", link: "/" },
    { name: "How it works", link: "/how-it-works" },
    { name: "Pricing", link: "/pricing" },
    {
      name: "Company",
      link: "#",
      dropdown: [
        { name: "Blog", link: "/blog" },
        { name: "About Us", link: "/about" },
        { name: "Careers", link: "/careers" },
        // { name: "Partners", link: "/partners" },
        { name: "FAQ", link: "/faq" },
      ],
    },
    // {
    //   name: "Resources",
    //   link: "#",
    //   dropdown: [
    //     { name: "Resume Tips", link: "/resume-tips" },
    //     { name: "Cover Letter Tips", link: "/cover-letter-tips" },
    //     { name: "Job Hunting Tips", link: "/job-hunting-tips" },
    //     { name: "Interview Tips", link: "/interview-tips" },
    //   ],
    // },
    { name: "Contact", link: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = () => setIsOpen(!isOpen);
  const toggleDropdown = (index) =>
    setActiveDropdown(activeDropdown === index ? null : index);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex-shrink-0">
            <RFLogo color={isScrolled ? "#4F46E5" : "#FFFFFF"} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <div key={index} className="relative group">
                {item.dropdown ? (
                  <button
                    onClick={() => toggleDropdown(index)}
                    className={`${
                      isScrolled ? "text-gray-800" : "text-gray-800"
                    } hover:text-indigo-600 transition duration-300 flex items-center text-sm font-medium`}
                  >
                    {item.name}
                    <svg
                      className={`w-4 h-4 ml-1 transform transition-transform duration-300 ${
                        activeDropdown === index ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                ) : (
                  <Link
                    to={item.link}
                    className={`${
                      isScrolled ? "text-gray-800" : "text-gray-800"
                    } hover:text-indigo-600 transition duration-300 text-sm font-medium`}
                  >
                    {item.name}
                  </Link>
                )}
                {item.dropdown && (
                  <AnimatePresence>
                    {activeDropdown === index && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10"
                      >
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.link}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition duration-150"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://app.resumeflex.com/auth"
              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-300"
            >
              Sign In
            </a>
            <a
              href="https://app.resumeflex.com/auth"
              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-300"
            >
              Get Started
            </a>
          </div>

          {/* Mobile Navigation Toggle */}
          <button
            className={`lg:hidden ${
              isScrolled ? "text-gray-800" : "text-gray-800"
            }`}
            onClick={toggleDrawer}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween" }}
            className="fixed inset-y-0 right-0 w-64 bg-white shadow-lg p-4 z-50 lg:hidden"
          >
            <button
              onClick={toggleDrawer}
              className="absolute top-4 right-4 text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="mt-8">
              {navItems.map((item, index) => (
                <div key={index} className="py-2">
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(index)}
                        className="w-full text-left py-2 text-gray-800 hover:text-indigo-600 transition duration-300 flex justify-between items-center text-sm font-medium"
                      >
                        {item.name}
                        <svg
                          className={`w-4 h-4 transform transition-transform duration-300 ${
                            activeDropdown === index ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <AnimatePresence>
                        {activeDropdown === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 mt-2 overflow-hidden"
                          >
                            {item.dropdown.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.link}
                                className="block py-2 text-gray-600 hover:text-indigo-600 transition duration-300 text-sm"
                                onClick={toggleDrawer}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.link}
                      className="block py-2 text-gray-800 hover:text-indigo-600 transition duration-300 text-sm font-medium"
                      onClick={toggleDrawer}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              {/* CTA Buttons */}
              <div className="flex flex-col items-center text-center justify-center gap-4">
                <a
                  href="https://app.resumeflex.com/auth"
                  className="bg-indigo-600 w-full text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-300"
                >
                  Sign In
                </a>
                <a
                  href="https://app.resumeflex.com/auth"
                  className="bg-indigo-600 w-full text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition duration-300"
                >
                  Get Started
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavMain;
